import { LinkedInPlatform, LinkedInIntegrationStatus, LinkedInPushStatus, LinkedInFailureReason } from '../enums';

export interface FieldMapping {
  platformField: string;
  shFieldId: number | null;
}

export interface LinkedInIntegration {
  id: number;
  platform: LinkedInPlatform;
  label: string;
  status: LinkedInIntegrationStatus;
  fieldMappings: FieldMapping[];
  lastVerifiedAt: string | null;
  createdAt: string;
  shAccountId?: number;
  userId?: number;
  encryptedApiKey?: string;
  apiKey?: string;
  user?: { id: number; firstName: string; lastName: string; email: string };
  notificationSent?: number;
  updatedAt?: string;
  deletedAt?: string | null;
}

export interface VerifyIntegrationResponse {
  id: number;
  status: number;
  lastVerifiedAt: string;
}

export interface LinkedInConfig {
  linkedinIntegrationId: number;
  externalCampaignId: string;
  heyreachLinkedinAccountId?: string | null;
}

export interface LinkedInCampaign {
  id: string;
  name: string;
  status: string;
  linkedInAccountIds?: number[];
}

export interface LinkedInAccount {
  id: number;
  emailAddress: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  profileUrl?: string;
}

export interface LinkedInStepConfig {
  id: number;
  stepId: number;
  sequenceId: number;
  linkedinIntegrationId: number;
  platform: LinkedInPlatform;
  externalCampaignId: string;
  campaignName: string;
  heyreachLinkedinAccountId: string | null;
}

export interface LinkedInStepConfigResponse {
  id: number;
  stepId: number;
  sequenceId: number;
  linkedinIntegrationId: number;
  platform: LinkedInPlatform;
  externalCampaignId: string;
  campaignName: string | null;
  heyreachLinkedinAccountId: string | null;
}

export interface LinkedInPushLog {
  id: number;
  taskId: number;
  status: LinkedInPushStatus;
  attemptNumber: number;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
}

export type ErrorReason =
  | 'Missing Required Field'
  | 'Active in Another Campaign'
  | 'Campaign Not Found'
  | 'API Key Invalid'
  | 'Invalid LinkedIn URL'
  | 'External Service Error'
  | 'Profile Not Ready';

export const FAILED_REASON_LABELS: Record<string, ErrorReason> = {
  LI_MISSING_REQUIRED_FIELD: 'Missing Required Field',
  LI_LOCKED_IN_CAMPAIGN: 'Active in Another Campaign',
  LI_CAMPAIGN_NOT_FOUND: 'Campaign Not Found',
  LI_ALREADY_CONNECTED: 'Active in Another Campaign',
  LI_INVALID_API_KEY: 'API Key Invalid',
  LI_INTEGRATION_INACTIVE: 'API Key Invalid',
  LI_INVALID_LINKEDIN_URL: 'Invalid LinkedIn URL',
  LI_RATE_LIMITED: 'External Service Error',
  LI_SERVER_ERROR: 'External Service Error',
  LI_FORBIDDEN: 'External Service Error',
  LI_MINING_NOT_COMPLETED: 'Profile Not Ready',
  LI_CAMPAIGN_NOT_ACTIVE: 'Campaign Not Active',
};

export interface FailedTaskDetail {
  id: number;
  type: number;
  platform: LinkedInPlatform;
  title: string;
  failedReason: LinkedInFailureReason;
  errorReason?: ErrorReason;
  campaignName: string;
  isAutomated: boolean;
  completedAt: string;
  prospectName: string;
  prospectEmail: string;
  source: string;
  sourceStep: string;
  taskOwner: string;
  taskOwnerInitials: string;
  prospectId?: number;
  sequenceId?: number;
  missingFields?: string[];
  fieldMappings?: FieldMapping[];
  currentLinkedInUrl?: string;
  rawApiError?: string;
}
